import { transitions } from "@/styles/transitions";

/**
 * Tailwindcss-Radix Utility Classes and Theme Integration
 *
 * This file documents the available utility classes from the tailwindcss-radix plugin
 * and our theme system for building components with Radix UI primitives.
 *
 * AVAILABLE THEME UTILITIES:
 * - text-theme-primary, text-theme-secondary, text-theme-accent
 * - bg-theme-primary, bg-theme-secondary, bg-theme-accent
 * - border-theme-primary, border-theme-secondary, border-theme-accent
 * - ring-theme-primary, ring-theme-secondary, ring-theme-accent
 *
 * All support opacity modifiers: text-theme-primary/50, bg-theme-accent/10
 *
 * TRANSITION UTILITIES:
 * - transition-standard (general transitions)
 * - transition-colors-standard (color/background/border)
 * - transition-transform-standard (transforms)
 * - transition-opacity-standard (opacity)
 *
 * @see https://github.com/ecklf/tailwindcss-radix
 * @see /src/styles/theme-utilities.md for complete documentation
 */

/**
 * ### State Variants
 *
 * - `radix-state-open:*` - For elements with `data-state="open"`
 * - `radix-state-closed:*` - For elements with `data-state="closed"`
 * - `radix-state-checked:*` - For elements with `data-state="checked"`
 * - `radix-state-unchecked:*` - For elements with `data-state="unchecked"`
 * - `radix-state-on:*` - For elements with `data-state="on"`
 * - `radix-state-off:*` - For elements with `data-state="off"`
 * - `radix-state-active:*` - For elements with `data-state="active"`
 * - `radix-state-inactive:*` - For elements with `data-state="inactive"`
 * - `radix-state-instant-open:*` - For elements with `data-state="instant-open"`
 * - `radix-state-delayed-open:*` - For elements with `data-state="delayed-open"`
 *
 * ### Side Variants
 *
 * - `radix-side-top:*` - For elements with `data-side="top"`
 * - `radix-side-right:*` - For elements with `data-side="right"`
 * - `radix-side-bottom:*` - For elements with `data-side="bottom"`
 * - `radix-side-left:*` - For elements with `data-side="left"`
 *
 * ### Orientation Variants
 *
 * - `radix-orientation-horizontal:*` - For elements with `data-orientation="horizontal"`
 * - `radix-orientation-vertical:*` - For elements with `data-orientation="vertical"`
 *
 * ### Other Variants
 *
 * - `radix-disabled:*` - For elements with the `disabled` attribute
 *
 * ### Group Modifiers
 *
 * - `group-radix-state-*` - For targeting elements inside a parent with `class="group"` and a state
 * - `group-radix-side-*` - For targeting elements inside a parent with `class="group"` and a side
 * - `group-radix-orientation-*` - For targeting elements inside a parent with `class="group"` and an orientation
 *
 * ### Peer Modifiers
 *
 * - `peer-radix-state-*` - For targeting elements after a sibling with `class="peer"` and a state
 * - `peer-radix-side-*` - For targeting elements after a sibling with `class="peer"` and a side
 * - `peer-radix-orientation-*` - For targeting elements after a sibling with `class="peer"` and an orientation
 */

/**
 * Origin position utilities
 */
export const originUtilities = {
  contextMenu: "origin-radix-context-menu", // transform-origin: var(--radix-context-menu-content-transform-origin)
  dropdownMenu: "origin-radix-dropdown-menu", // transform-origin: var(--radix-dropdown-menu-content-transform-origin)
  hoverCard: "origin-radix-hover-card", // transform-origin: var(--radix-hover-card-content-transform-origin)
  menubar: "origin-radix-menubar", // transform-origin: var(--radix-menubar-content-transform-origin)
  popover: "origin-radix-popover", // transform-origin: var(--radix-popover-content-transform-origin)
  select: "origin-radix-select", // transform-origin: var(--radix-select-content-transform-origin)
  tooltip: "origin-radix-tooltip", // transform-origin: var(--radix-tooltip-content-transform-origin)
};

/**
 * Content/Viewport Width/Height utilities
 */
export const contentSizeUtilities = {
  accordion: {
    height: "h-radix-accordion-content",
    width: "w-radix-accordion-content",
  },
  collapsible: {
    height: "h-radix-collapsible-content",
    width: "w-radix-collapsible-content",
  },
  navigationMenu: {
    height: "h-radix-navigation-menu-viewport",
    width: "w-radix-navigation-menu-viewport",
  },
};

/**
 * Content Available Width/Height utilities
 */
export const contentAvailableUtilities = {
  contextMenu: {
    height: "h-radix-context-menu-content-available",
    maxHeight: "max-h-radix-context-menu-content-available",
    maxWidth: "max-w-radix-context-menu-content-available",
    width: "w-radix-context-menu-content-available",
  },
  dropdownMenu: {
    height: "h-radix-dropdown-menu-content-available",
    maxHeight: "max-h-radix-dropdown-menu-content-available",
    maxWidth: "max-w-radix-dropdown-menu-content-available",
    width: "w-radix-dropdown-menu-content-available",
  },
  hoverCard: {
    height: "h-radix-hover-card-content-available",
    maxHeight: "max-h-radix-hover-card-content-available",
    maxWidth: "max-w-radix-hover-card-content-available",
    width: "w-radix-hover-card-content-available",
  },
  menubar: {
    height: "h-radix-menubar-content-available",
    maxHeight: "max-h-radix-menubar-content-available",
    maxWidth: "max-w-radix-menubar-content-available",
    width: "w-radix-menubar-content-available",
  },
  popover: {
    height: "h-radix-popover-content-available",
    maxHeight: "max-h-radix-popover-content-available",
    maxWidth: "max-w-radix-popover-content-available",
    width: "w-radix-popover-content-available",
  },
  select: {
    height: "h-radix-select-content-available",
    maxHeight: "max-h-radix-select-content-available",
    maxWidth: "max-w-radix-select-content-available",
    width: "w-radix-select-content-available",
  },
  tooltip: {
    height: "h-radix-tooltip-content-available",
    maxHeight: "max-h-radix-tooltip-content-available",
    maxWidth: "max-w-radix-tooltip-content-available",
    width: "w-radix-tooltip-content-available",
  },
};

/**
 * Trigger Available Width/Height utilities
 */
export const triggerSizeUtilities = {
  contextMenu: {
    height: "h-radix-context-menu-trigger",
    width: "w-radix-context-menu-trigger",
  },
  dropdownMenu: {
    height: "h-radix-dropdown-menu-trigger",
    width: "w-radix-dropdown-menu-trigger",
  },
  hoverCard: {
    height: "h-radix-hover-card-trigger",
    width: "w-radix-hover-card-trigger",
  },
  menubar: {
    height: "h-radix-menubar-trigger",
    width: "w-radix-menubar-trigger",
  },
  popover: {
    height: "h-radix-popover-trigger",
    width: "w-radix-popover-trigger",
  },
  select: {
    height: "h-radix-select-trigger",
    width: "w-radix-select-trigger",
  },
  tooltip: {
    height: "h-radix-tooltip-trigger",
    width: "w-radix-tooltip-trigger",
  },
};

/**
 * Toast Swiping utilities
 */
export const toastSwipeUtilities = {
  swipeEndX: "translate-x-radix-toast-swipe-end-x",
  swipeEndY: "translate-y-radix-toast-swipe-end-y",
  swipeMoveX: "translate-x-radix-toast-swipe-move-x",
  swipeMoveY: "translate-y-radix-toast-swipe-move-y",
};

/**
 * Standardized transition utilities to use with Radix
 *
 * When building Radix components, use these standardized utility classes:
 * - transition-standard: General transitions for most elements
 * - transition-colors-standard: For color/background/border transitions
 * - transition-transform-standard: For transform transitions
 * - transition-opacity-standard: For opacity transitions
 *
 * These classes use the standardized timing variables from theme-plugin-transitions.ts
 */

/**
 * Common used combinations for Radix UI components
 */
export const commonRadixStyles = {
  // Collapsible styling - Modern, theme-aware design
  collapsible: {
    // Border styles - Ultra-light with theme accents
    bordered: [
      "border",
      "border-gray-950/[0.06]",
      "dark:border-white/[0.08]",
      "rounded-lg",
      "overflow-hidden",
    ].join(" "),
    // Content area - Thoughtful styling with proper hierarchy
    content: [
      "overflow-hidden",
      "transition-all",
      "data-[state=open]:animate-collapsible-down",
      "data-[state=closed]:animate-collapsible-up",
      // Text styling for content
      "text-gray-600",
      "dark:text-gray-400",
      "text-sm",
    ].join(" "),
    // Icon - Refined with group hover states
    icon: [
      "h-4",
      "w-4",
      "shrink-0",
      "text-gray-400",
      "dark:text-gray-500",
      "transition-all",
      "duration-200",
      "ease-out",
      // Group hover effect
      "group-hover:text-gray-600",
      "dark:group-hover:text-gray-300",
    ].join(" "),
    // Icon rotation
    iconOpen: "rotate-180",
    // Size variants
    sizes: {
      lg: {
        icon: "h-5 w-5",
        trigger: "py-4 px-5 text-lg",
      },
      md: {
        icon: "h-4 w-4",
        trigger: "py-3 px-4",
      },
      sm: {
        icon: "h-3.5 w-3.5",
        trigger: "py-2 px-3 text-sm",
      },
    },
    // Trigger button - Minimal with thoughtful interactive states
    trigger: [
      "group",
      "flex",
      "w-full",
      "items-center",
      "justify-between",
      "py-3",
      "px-4",
      "font-medium",
      "text-left",
      "text-gray-700",
      "dark:text-gray-200",
      transitions.colors,
      // Subtle hover state
      "hover:text-gray-900",
      "dark:hover:text-white",
      // Theme-aware focus state
      "focus:outline-none",
      "focus-visible:ring-2",
      "focus-visible:ring-theme-primary/20",
      "focus-visible:ring-offset-1",
      "dark:focus-visible:ring-theme-primary/30",
      "focus-visible:ring-offset-transparent",
      "rounded-lg",
      "cursor-pointer",
    ].join(" "),
    // Different variants for varied use cases
    variants: {
      // Bordered variant - Theme-aware with borders (was default)
      bordered: {
        content: [
          "p-4",
          // Very subtle theme tint
          "bg-theme-primary/[0.01]",
          "dark:bg-theme-primary/[0.02]",
        ].join(" "),
        trigger: [
          // Very subtle theme background
          "bg-theme-primary/[0.02]",
          "dark:bg-theme-primary/[0.05]",
          "hover:bg-theme-primary/[0.04]",
          "dark:hover:bg-theme-primary/[0.08]",
          // Theme border
          "border",
          "border-theme-primary/10",
          "dark:border-theme-primary/20",
          // Text stays neutral for readability
          "text-gray-800",
          "dark:text-gray-100",
          // Theme accent on open
          "radix-state-open:border-theme-primary/30",
          "dark:radix-state-open:border-theme-primary/40",
        ].join(" "),
      },
      // Card variant - Elevated with shadows
      card: {
        content: [
          "p-4",
          // Subtle inset feel
          "bg-gray-50/30",
          "dark:bg-black/20",
        ].join(" "),
        trigger: [
          "bg-white",
          "dark:bg-gray-950",
          "shadow-sm",
          "hover:shadow-md",
          "dark:shadow-black/20",
          // Theme accent on hover
          "hover:border-theme-primary/20",
          "dark:hover:border-theme-primary/30",
          // Already has border from base bordered style
        ].join(" "),
      },
      // Default variant - Minimal, no borders (good for sidebars)
      default: {
        content: ["px-4", "pb-4", "pt-1"].join(" "),
        trigger: [
          "hover:bg-gray-950/[0.04]",
          "dark:hover:bg-white/[0.04]",
          // Subtle open state
          "radix-state-open:bg-gray-950/[0.02]",
          "dark:radix-state-open:bg-white/[0.02]",
        ].join(" "),
      },
      // Ghost variant - Completely invisible until hover
      ghost: {
        content: ["pl-4", "pr-4", "pb-3", "pt-1"].join(" "),
        trigger: [
          "hover:bg-gray-950/[0.02]",
          "dark:hover:bg-white/[0.02]",
        ].join(" "),
      },
      // Soft variant - Subtle gray backgrounds
      soft: {
        content: [
          "px-4",
          "pb-4",
          "pt-2",
          // Slightly inset background
          "bg-gray-50/50",
          "dark:bg-white/[0.01]",
        ].join(" "),
        trigger: [
          "bg-gray-100/50",
          "dark:bg-white/[0.02]",
          "hover:bg-gray-100",
          "dark:hover:bg-white/[0.04]",
          // Subtle border for definition
          "border",
          "border-gray-950/[0.06]",
          "dark:border-white/[0.06]",
        ].join(" "),
      },
    },
  },

  // Dialog/Modal styling
  dialog: {
    content: [
      "fixed",
      "left-[50%]",
      "top-[50%]",
      "z-50",
      "grid",
      "w-full",
      "max-w-lg",
      "translate-x-[-50%]",
      "translate-y-[-50%]",
      "gap-4",
      "border",
      "border-gray-200",
      "dark:border-gray-800",
      "bg-white",
      "dark:bg-gray-950",
      "p-6",
      "shadow-lg",
      "duration-200",
      "radix-state-open:animate-in",
      "radix-state-closed:animate-out",
      "radix-state-open:fade-in-0",
      "radix-state-closed:fade-out-0",
      "radix-state-open:zoom-in-95",
      "radix-state-closed:zoom-out-95",
      "radix-state-open:slide-in-from-left-1/2",
      "radix-state-open:slide-in-from-top-[48%]",
      "radix-state-closed:slide-out-to-left-1/2",
      "radix-state-closed:slide-out-to-top-[48%]",
      "rounded-lg",
    ].join(" "),
    description: "text-sm text-gray-500 dark:text-gray-400",
    overlay: [
      "fixed",
      "inset-0",
      "z-50",
      "bg-black/50",
      "backdrop-blur-sm",
      "radix-state-open:animate-in",
      "radix-state-closed:animate-out",
      "radix-state-open:fade-in-0",
      "radix-state-closed:fade-out-0",
    ].join(" "),
    title: "text-lg font-semibold leading-none tracking-tight",
  },

  // Label styling
  label: {
    // Disabled state
    disabled: "opacity-50 cursor-not-allowed",
    // Optional indicator
    optional: "text-gray-500 dark:text-gray-400 ml-1 text-xs",
    // Required indicator
    required: "text-red-500 ml-0.5",
    // Base label style
    root: [
      "text-sm",
      "font-medium",
      "text-gray-700",
      "dark:text-gray-300",
      "select-none",
    ].join(" "),
    // Sizes
    sizes: {
      lg: "text-base",
      md: "text-sm",
      sm: "text-xs",
    },
  },
  // Dropdown/Menu styling
  menu: {
    content: [
      "rounded-md",
      "border",
      "border-gray-200",
      "dark:border-gray-800",
      "bg-white",
      "dark:bg-gray-950",
      "p-1",
      "shadow-lg",
      "animate-in",
      "data-[side=bottom]:slide-in-from-top-2",
      "data-[side=top]:slide-in-from-bottom-2",
      "origin-radix-dropdown-menu",
    ].join(" "),
    item: [
      "relative",
      "flex",
      "cursor-default",
      "select-none",
      "items-center",
      "rounded-sm",
      "px-2",
      "py-1.5",
      "text-sm",
      "outline-none",
      transitions.colors,
      "radix-disabled:opacity-50",
      "radix-disabled:pointer-events-none",
      "radix-highlighted:bg-gray-100",
      "radix-highlighted:dark:bg-gray-800",
      "radix-highlighted:text-gray-900",
      "radix-highlighted:dark:text-gray-50",
    ].join(" "),
    label: "px-2 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400",
    separator: "h-px my-1 bg-gray-200 dark:bg-gray-800",
    shortcut:
      "ml-auto text-xs tracking-widest text-gray-500 dark:text-gray-400",
    trigger: "group",
  },

  // Slider styling
  slider: {
    // Slider range (colored active section)
    range: ["absolute", "rounded-full", "h-full", "bg-theme-primary"].join(" "),
    // Root container
    root: [
      "relative",
      "flex",
      "items-center",
      "select-none",
      "touch-none", // Prevent touch default behaviors on mobile
      "w-full",
    ].join(" "),
    // Slider thumb (draggable handle)
    thumb: [
      "block",
      "bg-white",
      "dark:bg-gray-800",
      "shadow-md",
      "rounded-full",
      "border",
      "border-gray-200",
      "dark:border-gray-600",
      "focus:outline-none",
      "focus:ring-2",
      "focus:ring-theme-primary",
      "cursor-grab",
      "active:cursor-grabbing",
      "touch-none", // Prevent touch default behaviors on mobile
    ].join(" "),
    // Slider track (background)
    track: [
      "relative",
      "grow",
      "rounded-full",
      "bg-gray-200",
      "dark:bg-gray-700",
    ].join(" "),
    // Value label displayed above thumbs
    valueLabel: [
      "inline-block",
      "px-2",
      "py-0.5",
      "text-xs",
      "font-medium",
      "rounded-full",
      "bg-gray-100",
      "dark:bg-gray-800",
      "text-gray-700",
      "dark:text-gray-300",
    ].join(" "),
  },

  // Switch styling
  switch: {
    // Root container
    root: [
      "relative",
      "inline-flex",
      "h-5",
      "w-10",
      "flex-shrink-0",
      "cursor-pointer",
      "rounded-full",
      "border-2",
      "border-transparent",
      transitions.colors,
      "focus:outline-none",
      "focus:ring-2",
      "focus:ring-theme-primary",
      "focus:ring-offset-2",
      "focus-visible:ring-2",
      "focus-visible:ring-theme-primary",
      "radix-disabled:opacity-50",
      "radix-disabled:cursor-not-allowed",
      "radix-state-checked:bg-theme-primary",
      "radix-state-unchecked:bg-gray-300",
      "dark:radix-state-unchecked:bg-gray-600",
    ].join(" "),
    // Size variants
    sizes: {
      lg: {
        root: "h-6 w-12",
        thumb: "h-5 w-5 radix-state-checked:translate-x-6",
      },
      md: {
        root: "h-5 w-10",
        thumb: "h-4 w-4 radix-state-checked:translate-x-5",
      },
      sm: {
        root: "h-4 w-8",
        thumb: "h-3 w-3 radix-state-checked:translate-x-4",
      },
    },
    // Thumb (the part that moves)
    thumb: [
      "pointer-events-none",
      "block",
      "h-4",
      "w-4",
      "rounded-full",
      "bg-white",
      "shadow-lg",
      "ring-0",
      "transition-transform",
      "radix-state-checked:translate-x-5",
      "radix-state-unchecked:translate-x-0",
    ].join(" "),
  },

  // Tooltip styling
  tooltip: {
    content: [
      "z-50",
      "overflow-hidden",
      "rounded-md",
      "border",
      "border-gray-200",
      "dark:border-gray-800",
      "bg-white",
      "dark:bg-gray-950",
      "px-3",
      "py-1.5",
      "text-sm",
      "text-gray-950",
      "dark:text-gray-50",
      "shadow-md",
      "animate-in",
      "fade-in-0",
      "zoom-in-95",
      "data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0",
      "data-[state=closed]:zoom-out-95",
      "data-[side=bottom]:slide-in-from-top-2",
      "data-[side=left]:slide-in-from-right-2",
      "data-[side=right]:slide-in-from-left-2",
      "data-[side=top]:slide-in-from-bottom-2",
    ].join(" "),
  },
};
